// ============= Imports =============
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Accordion, Button } from '@/ui';
import { FiMessageSquare } from 'react-icons/fi';
import { AuthContextProps } from '@/contexts/AuthContext';
import { COMPACT_VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/compactMessages';
import { CREATE_PROMPT_MESSAGES } from '@/constants/visualPrompts/createPromptMessages';
import { VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/messages';
import { PROMPT_CATEGORY_FILTERS } from '@/constants/visualPrompts/promptCategories';
import { useVisualPrompts } from '@/hooks/visualPrompts/useVisualPrompts';
import { useRunVisualPrompt } from '@/hooks/visualPrompts/useRunVisualPrompt';
import { buildReviewContext } from '@/utils/visualPrompts/buildReviewContext';
import type {
  VisualPromptAnswers,
  VisualPromptDefinition,
} from '@/types/visualPrompts/visualPrompt.types';
import {
  IntroBody,
  IntroCard,
  IntroTitle,
  SectionHeading,
  SectionHint,
} from '@/components/pages/workspaceDashboard/WorkspaceDashboard.styles';
import { WorkspaceOverviewEyebrow } from '@/components/pages/workspacePage/WorkSpacePage.styles';
import { PlaceholderText } from '@/components/pages/workspacePage/WorkSpacePage.styles';
import { CreateVisualPromptCard } from '../../CreateVisualPromptCard';
import { CreateVisualPromptModal } from '../../CreateVisualPromptModal';
import { DeleteVisualPromptModal } from '../../DeleteVisualPromptModal';
import { CompactVisualPromptCardGrid } from '../CompactVisualPromptCardGrid';
import { CompactVisualPromptSkeleton } from '../CompactVisualPromptSkeleton';
import { CompactVisualPromptWizard } from '../CompactVisualPromptWizard';
import {
  CategoryPill,
  CategoryPills,
  ErrorRow,
  FooterLink,
  HowItWorksList,
  IntroDivider,
  SearchInput,
  AccordionBody,
  AllPromptsAccordion,
  CreatePromptRow,
  SubsectionLabel,
  Toolbar,
  ToolbarRow,
  WorkspaceField,
  WorkspaceHint,
  WorkspaceSelect,
} from './styles';

// ============= Types =============
export interface CompactWorkspaceOption {
  id: string;
  title: string;
}

export interface CompactVisualPromptsSectionProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaces: CompactWorkspaceOption[];
  onCreateWorkspace: () => void;
}

// ============= Component =============
export const CompactVisualPromptsSection: React.FC<
  CompactVisualPromptsSectionProps
> = ({ getAccessToken, workspaces, onCreateWorkspace }) => {
  const navigate = useNavigate();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');

  useEffect(() => {
    if (workspaces.length === 0) {
      setSelectedWorkspaceId('');
      return;
    }
    if (!workspaces.some((w) => w.id === selectedWorkspaceId)) {
      setSelectedWorkspaceId(workspaces[0].id);
    }
  }, [workspaces, selectedWorkspaceId]);

  const selectedWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId);

  const {
    prompts,
    isLoading,
    error,
    filters,
    fetchPrompts,
    updateFilters,
    toggleFeatured,
  } = useVisualPrompts();
  const { runVisualPrompt, isRunning } = useRunVisualPrompt({
    workspaceId: selectedWorkspaceId,
    getAccessToken,
  });

  const [activeDefinition, setActiveDefinition] =
    useState<VisualPromptDefinition | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] =
    useState<VisualPromptDefinition | null>(null);

  const categoryOptions = useMemo(
    () => PROMPT_CATEGORY_FILTERS.filter((c) => c.id !== 'all'),
    [],
  );

  const { customPrompts, featuredPrompts, otherPrompts } = useMemo(() => {
    const custom: VisualPromptDefinition[] = [];
    const featured: VisualPromptDefinition[] = [];
    const other: VisualPromptDefinition[] = [];
    prompts.forEach((p) => {
      if (p.isCustom) {
        custom.push(p);
      }
      if (p.isFeatured) {
        featured.push(p);
      } else if (!p.isCustom) {
        other.push(p);
      }
    });
    return {
      customPrompts: custom,
      featuredPrompts: featured,
      otherPrompts: other,
    };
  }, [prompts]);

  const promptsDisabled = workspaces.length === 0;

  const openWizard = (definition: VisualPromptDefinition) => {
    if (promptsDisabled) {
      return;
    }
    setActiveDefinition(definition);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setActiveDefinition(null);
  };

  const requestDelete = (definition: VisualPromptDefinition) => {
    setPromptToDelete(definition);
  };

  const handlePromptDeleted = () => {
    if (promptToDelete && activeDefinition?.id === promptToDelete.id) {
      closeWizard();
    }
    void fetchPrompts();
  };

  const handleRun = async (
    definition: VisualPromptDefinition,
    answers: VisualPromptAnswers,
  ) => {
    if (!selectedWorkspaceId) {
      return;
    }
    const ok = await runVisualPrompt(definition, answers);
    if (ok) {
      closeWizard();
    }
  };

  const openLibrary = () => {
    if (selectedWorkspaceId) {
      navigate(`/workspace/${selectedWorkspaceId}/prompt-library`);
      return;
    }
    const first = workspaces[0]?.id;
    if (first) {
      navigate(`/workspace/${first}/prompt-library`);
    }
  };

  const reviewContext = buildReviewContext(
    {},
    selectedWorkspace?.title,
  );

  return (
    <>
      <IntroCard>
        {/* TODO: Not sure if i like these or not, not sure if it's just stating the obvious. */}
        {/* <WorkspaceOverviewEyebrow>
          <FiMessageSquare />
          {COMPACT_VISUAL_PROMPT_MESSAGES.eyebrow}
        </WorkspaceOverviewEyebrow> */}
        <IntroTitle>{COMPACT_VISUAL_PROMPT_MESSAGES.sectionTitle}</IntroTitle>
        <IntroBody>
          {COMPACT_VISUAL_PROMPT_MESSAGES.sectionDescription}
        </IntroBody>
        <IntroBody style={{ fontSize: '0.82rem' }}>
          {VISUAL_PROMPT_MESSAGES.oneTimeUseNote}
        </IntroBody>

        <IntroDivider />

        <SectionHeading as="div">
          {COMPACT_VISUAL_PROMPT_MESSAGES.howItWorksTitle}
        </SectionHeading>
        <HowItWorksList>
          {COMPACT_VISUAL_PROMPT_MESSAGES.howItWorksSteps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </HowItWorksList>

        <IntroDivider />

        <SectionHeading>Available prompts</SectionHeading>
        <SectionHint>
          Pick a task below—no prompt engineering required.
        </SectionHint>

        {promptsDisabled ? (
          <>
            <PlaceholderText>
              {COMPACT_VISUAL_PROMPT_MESSAGES.noWorkspaces}
            </PlaceholderText>
            <Button type="button" onClick={onCreateWorkspace}>
              {COMPACT_VISUAL_PROMPT_MESSAGES.createWorkspaceCta}
            </Button>
          </>
        ) : (
          <>
            <Toolbar>
              <ToolbarRow>
                <WorkspaceField>
                  {COMPACT_VISUAL_PROMPT_MESSAGES.workspaceLabel}
                  <WorkspaceHint>
                    {COMPACT_VISUAL_PROMPT_MESSAGES.workspaceHint}
                  </WorkspaceHint>
                  <WorkspaceSelect
                    value={selectedWorkspaceId}
                    onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                  >
                    {workspaces.map((ws) => (
                      <option key={ws.id} value={ws.id}>
                        {ws.title}
                      </option>
                    ))}
                  </WorkspaceSelect>
                </WorkspaceField>
              </ToolbarRow>
              <CategoryPills>
                <CategoryPill
                  type="button"
                  $active={(filters.category ?? 'all') === 'all'}
                  onClick={() => updateFilters({ category: 'all' })}
                >
                  {VISUAL_PROMPT_MESSAGES.allCategories}
                </CategoryPill>
                {categoryOptions.map((cat) => (
                  <CategoryPill
                    key={cat.id}
                    type="button"
                    $active={filters.category === cat.id}
                    onClick={() => updateFilters({ category: cat.id })}
                  >
                    {cat.label}
                  </CategoryPill>
                ))}
              </CategoryPills>
            </Toolbar>

            {isLoading ? (
              <CompactVisualPromptSkeleton />
            ) : error ? (
              <ErrorRow>
                <span>
                  {VISUAL_PROMPT_MESSAGES.loadError}: {error.message}
                </span>
                <Button variant="ghost" onClick={() => void fetchPrompts()}>
                  {VISUAL_PROMPT_MESSAGES.loadErrorRetry}
                </Button>
              </ErrorRow>
            ) : prompts.length === 0 ? (
              <PlaceholderText>
                {VISUAL_PROMPT_MESSAGES.emptyList}
              </PlaceholderText>
            ) : (
              <>
                <CreatePromptRow>
                  <CreateVisualPromptCard
                    disabled={promptsDisabled}
                    onClick={() => setCreateModalOpen(true)}
                  />
                </CreatePromptRow>

                {customPrompts.length > 0 && (
                  <>
                    <SubsectionLabel>
                      {CREATE_PROMPT_MESSAGES.yourPrompts}
                    </SubsectionLabel>
                    <CompactVisualPromptCardGrid
                      prompts={customPrompts}
                      onSelectPrompt={openWizard}
                      onDeletePrompt={requestDelete}
                      onToggleFeatured={toggleFeatured}
                      disabled={promptsDisabled}
                    />
                  </>
                )}

                {featuredPrompts.length > 0 && (
                  <>
                    <SearchInput
                      type="search"
                      placeholder={VISUAL_PROMPT_MESSAGES.searchPlaceholder}
                      value={filters.search ?? ''}
                      onChange={(e) =>
                        updateFilters({ search: e.target.value })
                      }
                    />
                    <SubsectionLabel>
                      {COMPACT_VISUAL_PROMPT_MESSAGES.quickAccess}
                    </SubsectionLabel>
                    <CompactVisualPromptCardGrid
                      prompts={featuredPrompts}
                      onSelectPrompt={openWizard}
                      onToggleFeatured={toggleFeatured}
                      disabled={promptsDisabled}
                    />
                  </>
                )}
                {otherPrompts.length > 0 && (
                  <AllPromptsAccordion>
                    <Accordion
                      title={`${COMPACT_VISUAL_PROMPT_MESSAGES.allPrompts} (${otherPrompts.length}) — ${COMPACT_VISUAL_PROMPT_MESSAGES.allPromptsAccordionHint}`}
                      extras={{ defaultExpanded: false }}
                    >
                      <AccordionBody>
                        <CompactVisualPromptCardGrid
                          prompts={otherPrompts}
                          onSelectPrompt={openWizard}
                          onToggleFeatured={toggleFeatured}
                          disabled={promptsDisabled}
                        />
                      </AccordionBody>
                    </Accordion>
                  </AllPromptsAccordion>
                )}
              </>
            )}

            <FooterLink>
              <Button type="button" variant="ghost" onClick={openLibrary}>
                {COMPACT_VISUAL_PROMPT_MESSAGES.viewAllInLibrary}
              </Button>
            </FooterLink>
          </>
        )}
      </IntroCard>

      <CreateVisualPromptModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSaved={() => void fetchPrompts()}
      />

      <DeleteVisualPromptModal
        definition={promptToDelete}
        onClose={() => setPromptToDelete(null)}
        onDeleted={handlePromptDeleted}
      />

      {wizardOpen && activeDefinition && selectedWorkspaceId && (
        <CompactVisualPromptWizard
          key={activeDefinition.id}
          isOpen={wizardOpen}
          definition={activeDefinition}
          onClose={closeWizard}
          onRun={handleRun}
          isRunning={isRunning}
          reviewContext={reviewContext}
        />
      )}
    </>
  );
};
